import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { useApi } from "../../lib/ApiContext";
import { validateCustomDomain } from "../../lib/validation";

const OrganizationNew = () => {
    const [formData, setFormData] = useState({
        name: '',
        custom_domain: '',
        default_destination: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const api = useApi();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name) {
            setError('Organization name is required');
            return;
        }

        // Validate custom domain
        const domainValidation = validateCustomDomain(formData.custom_domain);
        if (!domainValidation.valid) {
            setError(domainValidation.message);
            return;
        }

        // Validate default destination URL
        if (!formData.default_destination) {
            setError('Default Destination URL is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const newOrganization = await api.organizations.create(formData);
            navigate(`/organizations/${newOrganization.public_id}/edit`);
        } catch (err) {
            console.error("Failed to create organization:", err);
            setError(err.message);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/organizations');
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">New Organization</h1>
            </div>

            {error && (
                <Alert variant="destructive" className="mb-6">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        {error}
                    </AlertDescription>
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Create Organization</CardTitle>
                    <CardDescription>
                        Add a new organization to the system
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="col-span-3"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="custom_domain" className="text-right">
                                Custom Domain
                            </Label>
                            <Input
                                id="custom_domain"
                                name="custom_domain"
                                value={formData.custom_domain}
                                onChange={handleChange}
                                type="url"
                                placeholder="https://try.company.com"
                                className="col-span-3"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="default_destination" className="text-right">
                                Default Destination
                            </Label>
                            <Input
                                id="default_destination"
                                name="default_destination"
                                value={formData.default_destination}
                                onChange={handleChange}
                                type="url"
                                placeholder="https://example.com/fallback"
                                className="col-span-3"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <div className="col-start-2 col-span-3">
                                <p className="text-xs text-muted-foreground">
                                    When a request arrives that matches no existing routes, users will be redirected to this URL.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter className="flex justify-end gap-2 pt-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="animate-spin mr-2">⚪</span>
                                    Creating...
                                </>
                            ) : 'Create Organization'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
};

export default OrganizationNew; 